import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  Brain,
  Loader2,
  Shield,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  RefreshCw,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_COLORS = {
  Low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  High: "bg-red-50 text-red-700 border-red-200",
};

const ALLOC_COLORS = ["#0F2744", "#3B82F6", "#10B981"];

export default function AIRecommendations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: res } = await axios.get(`${API}/recommendations`);
      setData(res);
    } catch {
      setError(true);
      toast.error("Failed to generate recommendations");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v);

  // Not loaded yet — show CTA
  if (!data && !loading) {
    return (
      <Card className="border-slate-200 bg-gradient-to-br from-[#0F2744] to-[#1E3A5F] text-white overflow-hidden" data-testid="ai-reco-cta">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-5 h-5" />
                <h3 className="font-bold text-lg">AI Investment Advisor</h3>
              </div>
              <p className="text-white/70 text-sm mb-4 max-w-md">
                Get personalized portfolio allocation recommendations powered by GPT-5.2.
                Our AI analyzes project scores, ROI, document readiness, and market factors.
              </p>
              <Button
                onClick={fetchRecommendations}
                className="bg-white text-[#0F2744] hover:bg-white/90 font-semibold"
                data-testid="generate-reco-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Generate Recommendations
              </Button>
              {error && (
                <p className="text-red-300 text-xs mt-2">Failed to load. Please try again.</p>
              )}
            </div>
            <div className="hidden md:flex items-center justify-center w-24 h-24 bg-white/5 rounded-2xl">
              <Brain className="w-12 h-12 text-white/30" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="ai-reco-loading">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-10 h-10 text-[#3B82F6] animate-spin mx-auto mb-3" />
          <p className="font-semibold text-slate-900">Analyzing Projects...</p>
          <p className="text-sm text-slate-500 mt-1">GPT-5.2 is evaluating scores, ROI, and market factors</p>
        </CardContent>
      </Card>
    );
  }

  // Recommendation loaded
  return (
    <Card className="border-slate-200" data-testid="ai-reco-results">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-[#3B82F6]" />
          <CardTitle className="text-base font-semibold text-slate-900">
            AI Investment Recommendation
          </CardTitle>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
            {data.model}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchRecommendations}
          className="text-slate-500 hover:text-slate-900"
          data-testid="refresh-reco-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Strategy + Confidence */}
        <div className="p-4 bg-slate-50 rounded-xl" data-testid="reco-strategy">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${RISK_COLORS[data.risk_level] || RISK_COLORS.Medium}`}>
              {data.risk_level} Risk
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-500">Confidence</span>
              <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#3B82F6] rounded-full"
                  style={{ width: `${data.confidence_score || 0}%` }}
                />
              </div>
              <span className="text-xs font-bold text-slate-700">{data.confidence_score}%</span>
            </div>
          </div>
          <p className="text-sm text-slate-700">{data.overall_strategy}</p>
        </div>

        {/* Allocation Cards */}
        <div className="space-y-2" data-testid="reco-allocations">
          {data.allocations?.map((alloc, i) => (
            <div
              key={alloc.symbol}
              className="p-3 border border-slate-200 rounded-xl hover:border-blue-200 transition-colors"
              data-testid={`reco-alloc-${alloc.symbol}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: ALLOC_COLORS[i % ALLOC_COLORS.length] }}
                  >
                    {alloc.allocation_pct}%
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-900">{alloc.project_name}</p>
                    <span className="text-xs font-mono text-slate-500">{alloc.symbol}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900">{fmt(alloc.amount_usd)}</p>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${RISK_COLORS[alloc.risk] || RISK_COLORS.Medium}`}>
                    {alloc.risk}
                  </span>
                </div>
              </div>
              {/* Allocation bar */}
              <div className="h-1.5 bg-slate-100 rounded-full mb-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${alloc.allocation_pct}%`,
                    backgroundColor: ALLOC_COLORS[i % ALLOC_COLORS.length],
                  }}
                />
              </div>
              <div className="flex items-start gap-1.5">
                <ChevronRight className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-slate-600">{alloc.reasoning}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Insights + Warnings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.key_insights?.length > 0 && (
            <div className="p-3 bg-blue-50 rounded-xl" data-testid="reco-insights">
              <div className="flex items-center gap-1.5 mb-2">
                <Lightbulb className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-blue-800">Key Insights</span>
              </div>
              <ul className="space-y-1.5">
                {data.key_insights.map((insight, i) => (
                  <li key={i} className="text-[11px] text-blue-700 flex items-start gap-1.5">
                    <TrendingUp className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.warnings?.length > 0 && (
            <div className="p-3 bg-amber-50 rounded-xl" data-testid="reco-warnings">
              <div className="flex items-center gap-1.5 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-semibold text-amber-800">Risk Warnings</span>
              </div>
              <ul className="space-y-1.5">
                {data.warnings.map((warning, i) => (
                  <li key={i} className="text-[11px] text-amber-700 flex items-start gap-1.5">
                    <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
